import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";

import Landing from "@/pages/marketing/Landing";
import Legal from "@/pages/marketing/Legal";
import SetupGuide from "@/pages/marketing/SetupGuide";
import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import InviteAccept from "@/pages/auth/InviteAccept";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import AppShell from "@/components/layout/AppShell";
import Dashboard from "@/pages/app/Dashboard";
import Composer from "@/pages/app/Composer";
import Approvals from "@/pages/app/Approvals";
import CalendarPage from "@/pages/app/CalendarPage";
import MediaLibrary from "@/pages/app/MediaLibrary";
import SocialAccounts from "@/pages/app/SocialAccounts";
import Analytics from "@/pages/app/Analytics";
import Notifications from "@/pages/app/Notifications";
import Settings from "@/pages/app/Settings";
import Campaigns from "@/pages/app/Campaigns";
import CampaignDetail from "@/pages/app/CampaignDetail";
import PublicReview from "@/pages/public/PublicReview";
import AdminConsole from "@/pages/admin/AdminConsole";

function RequireAuth({ children }) {
  const { me, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="flex h-screen items-center justify-center text-[#43535E]">Loading…</div>;
  if (!me) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  return children;
}

function RequireSuperAdmin({ children }) {
  const { isSuperAdmin, loading, me } = useAuth();
  if (loading) return <div className="flex h-screen items-center justify-center text-[#43535E]">Loading…</div>;
  if (!me) return <Navigate to="/login" replace />;
  if (!isSuperAdmin) return <Navigate to="/app" replace />;
  return children;
}

function App() {
  useEffect(() => {
    document.title = "SocialMind — Think Bigger. Post Smarter. Grow Faster.";
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/privacy" element={<Legal />} />
          <Route path="/terms" element={<Legal />} />
          <Route path="/security" element={<Legal />} />
          <Route path="/gdpr" element={<Legal />} />
          <Route path="/setup-guide" element={<SetupGuide />}
          />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/invite" element={<InviteAccept />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/r/:token" element={<PublicReview />} />

          <Route path="/app" element={<RequireAuth><AppShell /></RequireAuth>}>
            <Route index element={<Dashboard />} />
            <Route path="composer" element={<Composer />} />
            <Route path="composer/:postId" element={<Composer />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="campaigns/:id" element={<CampaignDetail />} />
            <Route path="approvals" element={<Approvals />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="media" element={<MediaLibrary />} />
            <Route path="social" element={<SocialAccounts />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          <Route path="/admin" element={<RequireSuperAdmin><AdminConsole /></RequireSuperAdmin>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster richColors position="bottom-right" closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
